import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePermission } from '../../components/PermissionGuard';

interface PendingProposal {
  id: number;
  proposalTitle?: string;
  proposalDescription?: string;
  proposer: {
    id: string;
    name: string;
    email: string;
  };
  imdfFile: {
    id: string;
    originalName: string;
  };
  submittedAt: string;
  modificationItems?: { id: number }[];
}

const ReviewDashboard: React.FC = () => {
  const { isAdmin } = usePermission();
  const [proposals, setProposals] = useState<PendingProposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hasAdminPermission = isAdmin();
    
    if (!hasAdminPermission) {
      setError('需要 Admin 權限才能存取審核功能');
      setIsLoading(false);
      return;
    }
    
    loadPendingProposals();
  }, []); // 只在組件掛載時執行一次

  const loadPendingProposals = async () => {
    setIsLoading(true);
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://imdf.sto-tek.com';
      const token = localStorage.getItem('token');
      const requestUrl = `${API_URL}/proposals/pending`;

      console.log('[DEBUG ReviewDashboard] Request URL:', requestUrl);
      console.log('[DEBUG ReviewDashboard] Token exists:', !!token);

      const response = await fetch(requestUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      console.log('[DEBUG ReviewDashboard] Response status:', response.status);
      console.log('[DEBUG ReviewDashboard] Response ok:', response.ok);

      if (response.ok) {
        const result = await response.json();
        console.log('[DEBUG ReviewDashboard] Success response:', result);
        setProposals(result.data);
      } else {
        const errorText = await response.text();
        console.log('[DEBUG ReviewDashboard] Error response:', errorText);
        
        let errorMessage = `載入待審核提案失敗 (${response.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorMessage;
        } catch {
          errorMessage = `${errorMessage}: ${errorText}`;
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      console.log('[DEBUG ReviewDashboard] Exception:', err);
      setError(`網路錯誤: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-TW');
  };

  const getTimeSinceSubmission = (submittedAt: string) => {
    const now = new Date();
    const submitted = new Date(submittedAt);
    const diffInHours = Math.floor((now.getTime() - submitted.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 24) {
      return `${diffInHours} 小時前`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      return `${diffInDays} 天前`;
    }
  };

  if (!isAdmin()) {
    return (
      <div className="p-8 text-center">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
          需要 Admin 權限才能存取審核功能
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">載入待審核提案中...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center">
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md">
          {error}
        </div>
        <button
          onClick={loadPendingProposals}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          重新載入
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* 頁面標題 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">IMDF 修改提案審核</h1>
        <p className="text-gray-600 mt-1">
          審核 Maintainer 提出的 IMDF 檔案修改建議
        </p>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">待審核提案</h3>
          <p className="text-3xl font-bold text-blue-600">{proposals.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">總修改項目</h3>
          <p className="text-3xl font-bold text-green-600">
            {proposals.reduce((total, proposal) => total + (proposal.modificationItems?.length || 0), 0)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">平均等待時間</h3>
          <p className="text-3xl font-bold text-orange-600">
            {proposals.length > 0 
              ? Math.round(proposals.reduce((total, proposal) => {
                  const hours = Math.floor((new Date().getTime() - new Date(proposal.submittedAt).getTime()) / (1000 * 60 * 60));
                  return total + hours;
                }, 0) / proposals.length)
              : 0
            } 小時
          </p>
        </div>
      </div>

      {/* 提案列表 */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">待審核提案列表</h2>
        </div>

        {proposals.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-lg mb-2">目前沒有待審核的提案</p>
            <p className="text-sm">當有 Maintainer 提交修改建議時，會在此處顯示</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {proposals.map((proposal) => (
              <div key={proposal.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900">
                        {proposal.proposalTitle || `提案 #${proposal.id}`}
                      </h3>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">
                        待審核
                      </span>
                      <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                        {proposal.modificationItems?.length || 0} 項修改
                      </span>
                    </div>

                    <div className="text-sm text-gray-600 space-y-1">
                      <div>
                        <span className="font-medium">IMDF 檔案:</span> {proposal.imdfFile.originalName}
                      </div>
                      <div>
                        <span className="font-medium">提案人:</span> {proposal.proposer.name} ({proposal.proposer.email})
                      </div>
                      <div>
                        <span className="font-medium">提交時間:</span> {formatDate(proposal.submittedAt)} 
                        <span className="ml-2 text-gray-500">({getTimeSinceSubmission(proposal.submittedAt)})</span>
                      </div>
                      {proposal.proposalDescription && (
                        <div>
                          <span className="font-medium">描述:</span> {proposal.proposalDescription}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <Link
                      to={`/proposals/review/${proposal.id}`}
                      className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 transition-colors"
                    >
                      開始審核
                    </Link>
                  </div>
                </div>

                {/* 快速預覽修改項目 */}
                {proposal.modificationItems && proposal.modificationItems.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500 mb-2">修改概覽:</p>
                    <div className="flex flex-wrap gap-2">
                      {proposal.modificationItems.slice(0, 5).map((item, index) => (
                        <span key={item.id} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          修改項目 {index + 1}
                        </span>
                      ))}
                      {proposal.modificationItems.length > 5 && (
                        <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">
                          +{proposal.modificationItems.length - 5} 更多
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 重新整理按鈕 */}
      <div className="mt-6 text-center">
        <button
          onClick={loadPendingProposals}
          className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
        >
          重新整理列表
        </button>
      </div>
    </div>
  );
};

export default ReviewDashboard;